import { useState, type DragEvent } from 'react';
import type { PhaseOfPlay } from '@4cm/core/phaseOfPlay';
import { DrillTimelineCard } from './DrillTimelineCard';

export interface TimelineDrill {
  name: string;
  phase?: PhaseOfPlay | null;
  duration_mins?: number | null;
}

/**
 * Optional resolver for the per-drill id used as the tactics-board
 * persistKey root (`tactics-drill-${drillId}`). When provided, each
 * chip renders a static thumbnail of its saved layout. When omitted,
 * the timeline renders without thumbnails (legacy / test contexts).
 */
export type DrillIdResolver = (drill: TimelineDrill, index: number) => string;

export interface DrillTimelineProps {
  drills: ReadonlyArray<TimelineDrill>;
  selectedIndex: number;
  onSelect: (index: number) => void;
  /**
   * Reorder callback fired when a chip is dragged onto another, or when
   * keyboard reorder swaps neighbours. The implementation hands back a
   * fully-reordered array so the parent never has to manage drag
   * indices itself.
   */
  onReorder: (next: TimelineDrill[]) => void;
  /**
   * Remove a drill from the timeline (keyboard: Delete / Backspace).
   * Optional — when absent, the remove keyboard binding is a no-op.
   */
  onRemove?: (index: number) => void;
  /**
   * Announce live-region message for assistive tech. Phase E a11y plumbs
   * this from `<SessionCanvas>` which owns the off-screen aria-live div.
   */
  onAnnounce?: (message: string) => void;
  /**
   * Stable drill-id resolver — when provided, each chip renders a
   * tactics-layout thumbnail (per Phase 5 — drill-level tactics).
   */
  drillIdFor?: DrillIdResolver;
}

/**
 * Bottom rail of the Session Canvas — one chip per drill. Drag-to-reorder,
 * click-to-select, full keyboard support (Tab → arrows → Enter / Delete).
 * Empty state nudges the coach to use Brain to author the first drill
 * (we don't try to seed canvas-only authoring here — Brain stays the
 * entry point even in canvas-first mode, per spec §3 Phase C: "Brain
 * integration kept — voice/type still drives structural changes").
 */
export function DrillTimeline({
  drills,
  selectedIndex,
  onSelect,
  onReorder,
  onRemove,
  onAnnounce,
  drillIdFor,
}: DrillTimelineProps) {
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const handleDragStart = (i: number) => (e: DragEvent<HTMLButtonElement>) => {
    setDraggingIndex(i);
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers need data set or the drag event won't fire on drop.
    try { e.dataTransfer.setData('text/plain', String(i)); } catch { /* ignore */ }
  };

  const handleDragOver = (e: DragEvent<HTMLButtonElement>) => {
    if (draggingIndex == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (targetIndex: number) => (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (draggingIndex == null || draggingIndex === targetIndex) {
      setDraggingIndex(null);
      return;
    }
    const next = drills.slice();
    const [moved] = next.splice(draggingIndex, 1);
    next.splice(targetIndex, 0, moved);
    onReorder(next);
    onAnnounce?.(
      `Drill ${draggingIndex + 1} moved to position ${targetIndex + 1}`,
    );
    setDraggingIndex(null);
  };

  const handleDragEnd = () => setDraggingIndex(null);

  const swap = (a: number, b: number) => {
    if (a < 0 || b < 0 || a >= drills.length || b >= drills.length) return;
    const next = drills.slice();
    [next[a], next[b]] = [next[b], next[a]];
    onReorder(next);
    onAnnounce?.(`Drill ${a + 1} moved to position ${b + 1}`);
  };

  const remove = (i: number) => {
    if (!onRemove) return;
    const name = drills[i]?.name ?? `Drill ${i + 1}`;
    onRemove(i);
    onAnnounce?.(`Removed ${name} from drill ${i + 1}`);
  };

  const select = (i: number) => {
    onSelect(i);
    const name = drills[i]?.name;
    if (name) onAnnounce?.(`Selected ${name}, drill ${i + 1} of ${drills.length}`);
  };

  if (drills.length === 0) {
    return (
      <div className="session-canvas__timeline-empty" data-testid="drill-timeline-empty">
        No drills yet. Ask Brain to draft the first one — the canvas will pick it up.
      </div>
    );
  }

  return (
    <ol
      className="session-canvas__timeline"
      data-testid="drill-timeline"
      aria-label="Session drill sequence"
    >
      {drills.map((drill, i) => (
        <li key={`${drill.name}-${i}`} className="session-canvas__timeline-item">
          <DrillTimelineCard
            index={i}
            total={drills.length}
            name={drill.name}
            drillId={drillIdFor ? drillIdFor(drill, i) : undefined}
            phase={drill.phase ?? null}
            durationMins={drill.duration_mins ?? null}
            active={i === selectedIndex}
            onSelect={() => select(i)}
            onMoveLeft={() => swap(i, i - 1)}
            onMoveRight={() => swap(i, i + 1)}
            onRemove={onRemove ? () => remove(i) : undefined}
            onDragStart={handleDragStart(i)}
            onDragOver={handleDragOver}
            onDrop={handleDrop(i)}
            onDragEnd={handleDragEnd}
          />
        </li>
      ))}
    </ol>
  );
}
